import { useState } from 'react';
import { IconAngleRight, IconCheckDefault } from '@iwoca/icons';
import { Modal } from '@iwoca/orion';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import clsx from 'clsx';
import { ButtonLink } from 'components/ButtonLink/ButtonLink';
import { RCFRepaymentSchedule } from 'components/RCFRepaymentSchedule/RCFRepaymentSchedule';
import { useNumberFormat } from 'hooks/useNumberFormat';
import styles from './RCFRepaymentInfo.module.css';

const BULLET_POINTS = [
  {
    id: 'easy-activation',
    title: () => t`Easy Activation`,
    body: () =>
      t`Your customer must withdraw at least €150 to access the full credit limit. Further withdrawals are then possible at any time free of charge and without a new application.`,
  },
  {
    id: 'repayment-free-period',
    title: () => t`12-month grace period`,
    body: () =>
      t`Your customer only pays interest on the amount withdrawn for 12 months.`,
  },
  {
    id: 'extension-possible',
    title: () => t`Extension possible after renewed credit check`,
    body: () =>
      t`The grace period can be extended by a further 12 months. Please note that iwoca reserves the right to close the credit line early. The customer will then repay the outstanding amount in 12 monthly installments.`,
  },
  {
    id: 'special-repayments',
    title: () => t`Special repayments are possible at any time`,
    body: () =>
      t`Your customer can make free special repayments at any time to save interest.`,
  },
];

type TProps = {
  offerId: string | undefined;
  maxAmount: number | undefined;
};

export const RCFRepaymentInfo = ({ offerId, maxAmount }: TProps) => {
  const [scheduleOpen, setScheduleOpen] = useState(false);

  const { formatCurrency } = useNumberFormat();

  const isEnabled = offerId && maxAmount;

  const handleButtonClick = () => {
    setScheduleOpen(true);
  };

  return (
    <div
      data-testid="rcf-repayment-info"
      className={clsx(styles.container, {
        [styles.containerDisabled]: !isEnabled,
      })}
    >
      <p className={styles.heading}>
        <Trans>Here's how it works:</Trans>
      </p>
      <ul className={styles.list}>
        {BULLET_POINTS.map(({ id, title, body }) => (
          <li key={id} className={styles.item}>
            <IconCheckDefault
              className={styles.checkIcon}
              width={20}
              height={20}
            />
            <div className={styles.itemContent}>
              <p className={styles.itemTitle}>{title()}</p>
              <p className={styles.itemBody}>{body()}</p>
            </div>
          </li>
        ))}
      </ul>

      <ButtonLink
        className={styles.repaymentScheduleButton}
        onClick={handleButtonClick}
      >
        <Trans>See repayment schedule</Trans>
        <IconAngleRight height={16} width={16} />
      </ButtonLink>
      <Modal
        isOpen={scheduleOpen}
        onClose={() => setScheduleOpen(false)}
        closeIcon
        className={styles.rcfRepaymentScheduleModal}
        title={t`Indicative payment plan`}
        subTitle={t`*Assuming the outstanding loan amount is ${formatCurrency(maxAmount)}, the plan updates each time your customer withdraws or repays funds.`}
      >
        {isEnabled && (
          <RCFRepaymentSchedule offerId={offerId} maxAmount={maxAmount} />
        )}
      </Modal>
    </div>
  );
};
